import type {
  CampaignCreationCapabilities,
  SmartCreationReviewEditChange,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";
import { type Entry, validBid } from "./target-entries";

/**
 * Diffs the staged selections of every ad group against the opening snapshot.
 * Returns null when an included ad group would lose its last target or a bid is invalid.
 */
export function buildTargetDialogChanges(
  baseline: SmartCreationReviewWorkspace,
  precise: boolean,
  positiveEntries: Record<string, Entry[]>,
  negativeEntries: Record<string, Entry[]>,
  bidLimits: CampaignCreationCapabilities["bid"] | undefined,
): SmartCreationReviewEditChange[] | null {
  const changes: SmartCreationReviewEditChange[] = [];
  const removals: SmartCreationReviewEditChange[] = [];
  for (const currentGroup of baseline.graph.adGroups) {
    const key = currentGroup.ref.lineageKey;
    const positive = positiveEntries[key] ?? [];
    const included = baseline.graph.campaigns.some(
      (campaign) =>
        campaign.ref.lineageKey === currentGroup.campaignLineageKey && !campaign.excluded.effective,
    );
    const automaticSingleton =
      precise &&
      baseline.graph.targets.some(
        (target) => target.adGroupLineageKey === key && target.type === "automatic",
      );
    if (
      (included && !positive.length && !automaticSingleton) ||
      positive.some((entry) => !validBid(entry.bid, bidLimits))
    )
      return null;
    const includedIds = new Set(positive.map((entry) => entry.id));
    for (const original of baseline.graph.targets.filter(
      (target) => target.adGroupLineageKey === key && !target.excluded.effective,
    ))
      if (!includedIds.has(original.ref.lineageKey))
        removals.push({ operation: "set_target_excluded", ref: original.ref, excluded: true });
    for (const entry of positive) {
      if (entry.addition)
        changes.push({
          operation: "add_target",
          adGroupRef: currentGroup.ref,
          target: { ...entry.addition, bid: Number(entry.bid) },
        });
      if (entry.original) {
        if (entry.original.excluded.effective)
          changes.push({
            operation: "set_target_excluded",
            ref: entry.original.ref,
            excluded: false,
          });
        if (Number(entry.bid) !== entry.original.bid.effective)
          changes.push({
            operation: "set_target_bid",
            ref: entry.original.ref,
            bid: Number(entry.bid),
          });
      }
    }
    const negative = negativeEntries[key] ?? [];
    for (const entry of negative)
      if (entry.negativeAddition)
        changes.push({
          operation: "add_negative",
          adGroupRef: currentGroup.ref,
          negative: entry.negativeAddition,
        });
    const negativeIds = new Set(negative.map((entry) => entry.id));
    for (const original of baseline.graph.negatives.filter(
      (target) => target.adGroupLineageKey === key,
    ))
      if (!negativeIds.has(original.ref.lineageKey))
        changes.push({ operation: "remove_negative", ref: original.ref });
  }
  changes.push(...removals);
  return changes;
}
